
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCreditCard, FaQrcode, FaTimes, FaLock, FaRedo } from 'react-icons/fa';
import { MyContext } from '../pages/context/UserProvider';

const getToken = () => {
  try {
    return JSON.parse(localStorage.getItem('usuario') || 'null')?.auth_token || null;
  } catch (_) {
    return null;
  }
};

const linkAsaasValido = (link) => {
  try {
    const url = new URL(link);
    return url.protocol === 'https:' &&
      (url.hostname === 'asaas.com' || url.hostname.endsWith('.asaas.com'));
  } catch (_) {
    return false;
  }
};

export default function CheckoutPayment() {
  const {
    setShowCheckoutPayment,
    opcaoDeParcela,
    setOpcaoDeParcela,
    orcamentosSelecionados
  } = useContext(MyContext);
  const [estado, setEstado] = useState('verificando');
  const [mensagem, setMensagem] = useState('');
  const [pendente, setPendente] = useState(null);
  const [carregando, setCarregando] = useState('');
  const requisicaoEmAndamento = useRef(false);
  const apiBase = process.env.REACT_APP_API_URL;
  const parcelas = [1, 2, 3, 4, 5, 6].includes(Number(opcaoDeParcela))
    ? Number(opcaoDeParcela)
    : 1;

  const verificarPendente = useCallback(async (signal) => {
    setEstado('verificando');
    setMensagem('');
    setPendente(null);

    const token = getToken();
    if (!token) {
      setEstado('bloqueado');
      setMensagem('Sua sessão não foi identificada. Faça login novamente.');
      return;
    }
    if (!apiBase) {
      setEstado('bloqueado');
      setMensagem('Endereço da API não configurado.');
      return;
    }

    try {
      const resposta = await fetch(`${apiBase}/api/get-pagamento-pendente-asaas-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
        signal,
      });
      const dados = await resposta.json().catch(() => null);

      if (!resposta.ok || !dados || dados.success !== true) {
        throw new Error(dados?.error || dados?.message || 'Falha ao consultar pagamentos pendentes.');
      }

      if (dados.has_pending === false) {
        setEstado('livre');
        return;
      }

      if (dados.has_pending === true && dados.state === 'ready' &&
        linkAsaasValido(dados.invoiceUrl)) {
        setPendente(dados);
        setEstado('pronto');
        return;
      }

      setEstado('bloqueado');
      if (dados.state === 'multiple_pending') {
        setMensagem('Há mais de um pedido pendente nesta conta. Precisamos conferir as cobranças antes de gerar outro pagamento.');
      } else if (dados.state === 'pending_confirmation') {
        setMensagem('Um pagamento anterior está aguardando confirmação. Não será criada outra cobrança.');
      } else {
        setMensagem('Existe um pedido que precisa de conferência antes de gerar outro pagamento.');
      }
    } catch (erro) {
      if (signal?.aborted) return;
      setEstado('bloqueado');
      setMensagem(erro.message || 'Não foi possível verificar os pagamentos pendentes.');
    }
  }, [apiBase]);

  useEffect(() => {
    const controller = new AbortController();
    verificarPendente(controller.signal);
    return () => controller.abort();
  }, [verificarPendente]);

  useEffect(() => {
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflowAnterior; };
  }, []);

  const fechar = useCallback(() => {
    if (!requisicaoEmAndamento.current) setShowCheckoutPayment(false);
  }, [setShowCheckoutPayment]);

  useEffect(() => {
    const esc = (event) => {
      if (event.key === 'Escape') fechar();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [fechar]);

  const criarPagamento = async (formaPagamento) => {
    if (estado !== 'livre' || requisicaoEmAndamento.current) return;

    if (
      !Array.isArray(orcamentosSelecionados) ||
      orcamentosSelecionados.length === 0
    ) {
      setEstado('bloqueado');
      setMensagem(
        'Nenhum produto selecionado. Feche esta janela e selecione os produtos que deseja comprar.'
      );
      return;
    }
    const token = getToken();
    if (!token || !apiBase) {
      setEstado('bloqueado');
      setMensagem('Sua sessão ou a configuração da API está indisponível.');
      return;
    }

    requisicaoEmAndamento.current = true;
    setCarregando(formaPagamento);
    setMensagem('');

    try {
      const resposta = await fetch(`${apiBase}/api/create-payment-asaas-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          forma_pagamento: formaPagamento,
          ...(formaPagamento === 'CREDIT_CARD' ? { parcelas } : {}),
          orcamento_ids: orcamentosSelecionados.map(Number),
        }),
      });
      const dados = await resposta.json().catch(() => null);

      if (resposta.status === 202 && dados?.pending_confirmation) {
        setEstado('bloqueado');
        setMensagem('A resposta do Asaas foi inconclusiva. Confira o pagamento antes de tentar novamente.');
        return;
      }

      if (!resposta.ok || !dados || dados.success !== true) {
        setEstado('bloqueado');
        setMensagem(dados?.error || dados?.message || 'Não foi possível confirmar a criação da cobrança.');
        return;
      }

      if (dados.status && dados.status !== 'PENDING') {
        setEstado('bloqueado');
        setMensagem('A cobrança já possui outro status no Asaas. Verifique o pedido antes de continuar.');
        return;
      }

      if (!linkAsaasValido(dados.invoiceUrl)) {
        setEstado('bloqueado');
        setMensagem('Não foi possível validar o link do pagamento. Verifique o pedido antes de tentar novamente.');
        return;
      }

      window.location.assign(dados.invoiceUrl);
    } catch (_) {
      setEstado('bloqueado');
      setMensagem('Não foi possível confirmar se a cobrança foi criada. Verifique novamente antes de tentar pagar.');
    } finally {
      requisicaoEmAndamento.current = false;
      setCarregando('');
    }
  };

  const abrirPagamentoExistente = () => {
    if (pendente && linkAsaasValido(pendente.invoiceUrl)) {
      window.location.assign(pendente.invoiceUrl);
    }
  };

  const botao = {
    width: '100%', padding: '13px 16px', border: 0, borderRadius: 9,
    color: '#fff', fontSize: 14, fontWeight: 650, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 9, boxSizing: 'border-box', minHeight: 46,
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-checkout-payment
      onMouseDown={(e) => { if (e.target === e.currentTarget) fechar(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        background: 'rgba(15, 16, 16, 0.63)', backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 16, overflowY: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-payment-title"
        style={{
          background: '#fff', color: '#25211f', width: '100%', maxWidth: 450,
          maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
          borderRadius: 18, padding: '23px 21px', boxSizing: 'border-box',
          boxShadow: '0 24px 80px rgba(0,0,0,0.26)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
          <div>
            <h2 id="checkout-payment-title" style={{ margin: '0 0 5px', fontSize: 22, fontWeight: 650 }}>
              Finalizar pagamento
            </h2>
            <p style={{ margin: 0, color: '#817872', fontSize: 13 }}>
              Escolha como deseja pagar
            </p>
          </div>
          <button
            type="button" onClick={fechar} disabled={Boolean(carregando)}
            aria-label="Fechar pagamento"
            style={{
              border: 0, background: '#f5f3f0', color: '#433b36',
              width: 33, height: 33, borderRadius: 8, cursor: carregando ? 'not-allowed' : 'pointer',
              flexShrink: 0, display: 'grid', placeItems: 'center'
            }}
          ><FaTimes size={13} /></button>
        </div>

        {estado === 'livre' ? (
          <>
            <div style={{ padding: 17, borderRadius: 13, border: '1px solid #b8dec5', background: '#f1faf4', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <span style={{ color: '#196b3c', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
                  <FaQrcode /> Pix à vista
                </span>
                <span style={{
                  color: '#196b3c', background: '#daf2e1', borderRadius: 6,
                  padding: '5px 8px', fontSize: 11, fontWeight: 750, whiteSpace: 'nowrap'
                }}>10% OFF</span>
              </div>
              <p style={{ fontSize: 12, color: '#58725f', margin: '0 0 14px' }}>
                Desconto calculado e aplicado pelo servidor.
              </p>
              <button type="button" disabled={Boolean(carregando)} onClick={() => criarPagamento('PIX')}
                style={{ ...botao, background: '#22874d', opacity: carregando ? 0.7 : 1 }}>
                <FaQrcode /> {carregando === 'PIX' ? 'Gerando Pix...' : 'Pagar com Pix'}
              </button>
            </div>

            <div style={{ padding: 17, borderRadius: 13, border: '1px solid #e7e1dc', background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, marginBottom: 11 }}>
                <FaCreditCard /> Cartão de crédito
              </div>
              <label htmlFor="checkout-parcelas" style={{
                display: 'block', fontSize: 12,
                color: '#6c625c', marginBottom: 7
              }}>Parcelamento em até 6x</label>
              <select
                id="checkout-parcelas" value={parcelas}
                onChange={(e) => setOpcaoDeParcela(Number(e.target.value))}
                disabled={Boolean(carregando)}
                style={{
                  width: '100%', border: '1px solid #ddd4cd', borderRadius: 8,
                  padding: '11px 12px', marginBottom: 13, background: '#fff',
                  color: '#312923', fontSize: 14, boxSizing: 'border-box'
                }}
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option value={n} key={n}>{n}x no cartão</option>
                ))}
              </select>
              <button type="button" disabled={Boolean(carregando)}
                onClick={() => criarPagamento('CREDIT_CARD')}
                style={{ ...botao, background: '#302820', opacity: carregando ? 0.7 : 1 }}>
                <FaCreditCard /> {carregando === 'CREDIT_CARD' ? 'Gerando cobrança...' : 'Pagar com cartão'}
              </button>
            </div>
          </>
        ) : (
          <div role="status" style={{
            border: '1px solid #e9e2dc', background: '#fcfaf8',
            borderRadius: 12, padding: 17, fontSize: 13, color: '#463c35'
          }}>
            {estado === 'verificando' ? (
              <p style={{ margin: 0 }}>Verificando se já existe um pagamento pendente...</p>
            ) : estado === 'pronto' ? (
              <>
                <p style={{ margin: '0 0 12px' }}>
                  Já existe uma cobrança pendente registrada para sua conta.
                  Você pode continuar esse pagamento sem gerar outro.
                </p>
                <button type="button" onClick={abrirPagamentoExistente}
                  style={{ ...botao, background: '#22874d' }}>Continuar pagamento existente</button>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 14px', lineHeight: 1.6 }}>{mensagem}</p>
                <button type="button" onClick={() => verificarPendente()}
                  style={{ ...botao, background: '#302820' }}>
                  <FaRedo size={12} /> Verificar novamente
                </button>
              </>
            )}
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 6, fontSize: 11, color: '#827871', marginTop: 18, textAlign: 'center'
        }}>
          <FaLock size={11} /> Pagamento processado pelo Asaas
        </div>
      </section>
    </div>,
    document.body
  );
}
